import React from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import { Box } from 'components/Layout'
import { H2, H3, LI, Paragraph, Strong } from 'components/Legal'
import { PureSSRIndicator } from 'components/PureSSRIndicator'
import { SPACE } from 'Theme'
import { prepareGetStaticNavigationProps } from '../utils/prepareGetStaticNavigationProps'

export const TradeProgramAgreement = () => (
  <Box mt={SPACE.PX_80} mb={SPACE.PX_120}>
    <H2>THE EXPERT TRADE PROGRAM AGREEMENT</H2>
    <H3>Effective: April 10, 2025</H3>
    <H3>Last Updated: January 12, 2026</H3>
    <br />
    <Paragraph>
      This Trade Program Agreement (this{' '}
      <Strong>&ldquo;Agreement&rdquo;</Strong>) sets forth the terms and
      conditions for the Trade Program offered by The Expert, Inc. (
      <Strong>&ldquo;The Expert&rdquo;</Strong>). This Agreement is incorporated
      into and made part of The Expert&apos;s Terms of Service available at{' '}
      <a
        href="https://www.theexpert.com/terms"
        target="_blank"
        rel="noreferrer"
      >
        https://www.theexpert.com/terms
      </a>{' '}
      (the <Strong>&ldquo;Terms&rdquo;</Strong>). Capitalized terms used in this
      Agreement and not defined have the meanings given to them in the Terms. In
      the event of any conflict between the terms and conditions of this
      Agreement and the Terms, this Agreement shall control solely with respect
      to the Trade Program.
    </Paragraph>
    <Paragraph>
      <Strong>IMPORTANT -- PLEASE READ THIS AGREEMENT CAREFULLY</Strong>. This
      Agreement is a legally binding contract governing the participation in the
      Trade Program, and applies to (a) the individual, entity, company,
      business or organization that is applying for the Trade Program (an{' '}
      <Strong>&ldquo;Applicant&rdquo;</Strong>), (b) the individual, entity,
      company, business or organization that has otherwise been approved by The
      Expert for the Trade Program and identified as the{' '}
      <Strong>&ldquo;Trade Member&rdquo;</Strong> on the account registered for
      such Trade Member, and (c) Team Members of the Trade Member (as defined in
      Section 3(a) below).{' '}
      <Strong>
        PLEASE NOTE, THIS AGREEMENT ALSO CONTAIN AN AGREEMENT TO ARBITRATE IN
        SECTION 14 WHICH WILL REQUIRE YOU TO SUBMIT CLAIMS YOU HAVE AGAINST US
        TO BINDING AND FINAL ARBITRATION.
      </Strong>
    </Paragraph>
    <Paragraph>
      <Strong>1. Acceptance of this Agreement.</Strong> By submitting a Trade
      Program application, registering an account to participate in the Trade
      Program (an <Strong>&ldquo;Account&rdquo;</Strong>), clicking “I Accept”
      or otherwise indicating your acceptance to this Agreement, or
      participating in the Trade Program in any manner, you: (1) acknowledge
      that you have read, understand, and agree to be bound by this Agreement
      (including,{' '}
      <a
        href="https://www.theexpert.com/terms"
        target="_blank"
        rel="noreferrer"
      >
        The Expert Terms of Service
      </a>{' '}
      and all of the terms and conditions specified or referenced below); (2)
      represent that you have the authority and have obtained all rights,
      permissions and authorizations to enter into the terms of this Agreement
      on behalf of the Applicant or Trade Member (as applicable) and to bind the
      Applicant or Trade Member to the terms of this Agreement; and (3)
      acknowledge and agree that the Applicant or Trade Member (as applicable)
      is entering into this Agreement with The Expert, Inc., a Delaware
      corporation. The terms “you” and “your” as used through this Agreement
      shall also refer to the Applicant and/or Trade Member, as applicable.
    </Paragraph>
    <Paragraph>
      You further represent and warrant that: (a) your acceptance and execution
      of this Agreement and performance hereunder will not constitute a breach
      of any other agreement; and (b) you will comply with applicable laws,
      rules, regulations and guidelines in connection with your participation in
      the Trade Program.
    </Paragraph>
    <Paragraph>
      <Strong>
        IF YOU DO NOT AGREE WITH ALL OF THE TERMS AND CONDITIONS OF THIS
        AGREEMENT, OR DO NOT HAVE THE REQUISITE AUTHORITY AS SET FORTH ABOVE, DO
        NOT ACCEPT THIS AGREEMENT.
      </Strong>
    </Paragraph>
    <Paragraph>
      <Strong>2. Applications and Eligibility.</Strong> Participation in the
      Trade Program is subject to The Expert&apos;s approval of each Applicant.
      Applicants must complete and submit The Expert&apos;s Trade Program
      application and provide all additional information requested by The Expert
      to be considered for approval to the Trade Program. By submitting an
      application, you represent, warrant and covenant that all information
      provided in the application is true, accurate, current, up to date, and
      complete. Upon approval of an Applicant by The Expert, such Applicant
      shall be deemed a “Trade Member” and eligible to participate in the Trade
      Program. If an Applicant is rejected, the Applicant shall not be eligible
      to participate in the Trade Program and this Agreement shall immediately
      and automatically terminate with respect to the Applicant. Any approvals
      and/or rejections of Applicants shall be at The Expert&apos;s sole
      discretion and decision.
    </Paragraph>
    <Paragraph>
      <Strong>3. Accounts</Strong>{' '}
    </Paragraph>
    <ol style={{ listStyleType: 'lower-alpha' }}>
      <LI>
        Each Trade Member must register an Account under the name of the
        applicable individual, entity, company, business or organization that is
        the Trade Member (the <Strong>&ldquo;Trade Account&rdquo;</Strong>
        ). Trade Members may authorize employees, contractors, agents and/or
        other representatives to register sub-Accounts as users under the Trade
        Account (collectively,
        <Strong>&ldquo;Team Members&rdquo;</Strong>).
      </LI>
      <LI>
        In registering a Trade Account and any sub-Accounts, Trade Member agrees
        to, and shall ensure that its Team Members, provide and maintain account
        registration information, which may include, name, location, business
        license or registration, e-mail address or other contact information,
        and billing information, that is true, accurate, current, up to date,
        and complete.{' '}
      </LI>
      <LI>
        Trade Member is solely responsible for any activity originating from the
        Trade Account, including, without limitation, any access of the Trade
        Account by Team Members and/or sub-Accounts created by Team Members
        under the Trade Account, regardless of whether such activity is
        authorized by Trade Member. Trade Member agrees to notify The Expert
        immediately of any unauthorized use of or access to the Trade Account or
        any Team Member&apos;s sub-Account.
      </LI>
      <LI>
        Trade Member and Team Members may not share their Accounts, any login
        credentials or passwords for such Accounts, or any benefits under the
        Trade Program with any other individual, business, entity, firm or other
        third party, unless otherwise expressly pre-approved by The Expert in
        writing. Trade Member acknowledges that sharing of any such rights is
        strictly prohibited. Trade Member&apos;s right to participate in the
        Trade Program is personal to Trade Member and not assignable or
        transferable. Trade Member may not assign or transfer the Trade Account
        or any sub-Account registered to a specific Team Member, with any other
        individual, business, entity, firm or other third party, unless
        otherwise expressly pre-approved by The Expert in writing.
      </LI>
    </ol>
    <Paragraph>
      <Strong>4. Account Administration & Responsibility</Strong>{' '}
    </Paragraph>
    <ol style={{ listStyleType: 'lower-alpha' }}>
      <LI>
        The Team Member that sets up the Trade Account during registration shall
        be designated as the primary user under the Trade Account (the{' '}
        <Strong>&ldquo;Primary User&rdquo;</Strong>) to administer and manage
        the Trade Account, which includes, without limitation, the right to
        invite other Team Members to register sub-Accounts and participate in
        the Trade Program under the Trade Account on behalf of the Trade Member.
        The Primary User may be changed to another Team Member by submitting a
        request to The Expert via email at: trade@theexpert.com, in which case
        such new Team Member shall be deemed the Primary User.
      </LI>
      <LI>
        Trade Member acknowledges and agrees that Customer is solely responsible
        and liable for its Primary User(s)&apos; management and administration
        of the Trade Account, including, but not limited to, the inviting Team
        Members to register sub-Accounts and participate in the Trade Program
        under the Trade Account.
      </LI>
      <LI>
        Trade Member agrees that it shall not permit any person, individual, or
        third party, other than Team Members to participate in the Trade
        Program, and shall ensure that Team Members comply with the terms of
        this Agreement at all times.
      </LI>
      <LI>
        Trade Member shall be responsible for its Team Members&apos; compliance
        with the terms and conditions of this Agreement and{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>
        , and any noncompliance with any of the terms and conditions set forth
        herein or in{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>{' '}
        by any Team Member shall be deemed a breach of this Agreement and/or{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>{' '}
        (as applicable) by Trade Member.
      </LI>
    </ol>
    <Paragraph>
      <Strong>5. Trade Program Benefits</Strong>{' '}
    </Paragraph>
    <ol style={{ listStyleType: 'lower-alpha' }}>
      <LI>
        <Strong>Trade Program Perks</Strong>
        <ol style={{ listStyleType: 'lower-roman' }}>
          <LI>
            Trade Members will be eligible to receive the Trade Program benefits
            and perks (the <Strong>&ldquo;Trade Perks&rdquo;</Strong>) based on
            the Trade Member&apos;s then-current status tier (“Status Tier”).
            The current Trade Perks, Status Tiers, and additional information
            regarding the Trade Perks and Status Tiers are available on our{' '}
            <a
              href="https://www.theexpert.com/trade#benefits"
              target="_blank"
              rel="noreferrer"
            >
              Trade Perks Page
            </a>
            . Restrictions apply as identified herein and as communicated in any
            Trade Program materials, including, but not limited to, the{' '}
            <a
              href="https://www.theexpert.com/trade#benefits"
              target="_blank"
              rel="noreferrer"
            >
              Trade Perks Page
            </a>
            .
          </LI>
          <LI>
            Except as otherwise stated, Trade Perks must be used in the calendar
            year in which they are awarded, and any unused Trade Perks will be
            forfeited at the end of each calendar year.
          </LI>
          <LI>
            At the beginning of each calendar year, a Trade Member&apos;s Trade
            Perks will be renewed based on their Status Tier at the end of the
            last year. If a Trade Member unlocks higher Status Tier, it will be
            entitled only to the Trade Perks associated with the Trade
            Member&apos;s new Status Tier. If any Trade Perks were used while at
            the Trade Member&apos;s previous Status Tier, those Trade Perks will
            be subtracted from the new Status Tier Trade Perks.
          </LI>
          <LI>
            Trade Perks (including, but not limited to any Trade Member
            discounts and/or Trade Notes described herein) are non-negotiable,
            have no cash value, cannot be exchanged for cash or credit, nor
            shall any substitutions or partial acceptance be permitted. Trade
            Perks may not be purchased or sold, and do not constitute property.
            Trade Members have no ownership interest in Trade Perks. The use of
            the word &quot;earn&quot; or any similar language in the Trade
            Program materials or this Agreement does not imply that Trade Perks
            have any value prior to redemption. Any receipt or use of Trade
            Perks in violation of this Agreement, will render such Trade Perks
            void.
          </LI>
          <LI>
            Trade Perks described in the{' '}
            <a
              href="https://www.theexpert.com/trade#benefits"
              target="_blank"
              rel="noreferrer"
            >
              Trade Perks Page
            </a>{' '}
            are provided, offered and developed and delivered at the sole
            discretion of The Expert, and are not guaranteed. Certain Trade
            Perks may require a purchase or an additional expense and/or be
            subject to additional terms, conditions, rules, policies or
            requirements.
          </LI>
        </ol>
      </LI>
      <LI>
        <Strong>Status Tiers</Strong>
        <ol style={{ listStyleType: 'lower-roman' }}>
          <LI>
            Status Tiers are unlocked based on the net balance of purchases made
            under the Trade Account or a Team Member&apos;s sub-Account through
            The Expert Marketplace or a Trade Vendor (
            <Strong>&ldquo;Net Spend&rdquo;</Strong>). Net Spend is the purchase
            amount after deducting amounts for returns, adjustments, shipping &
            handling fees and costs, discounts, credits, taxes, and any amounts
            redeemed in Trade Notes (defined below), gift cards or other credits
            for such purchase. For clarity, a Trade Member&apos;s Net Spend will
            be based on eligible purchases made by the Trade Member and its Team
            Members under the Trade Account and associated sub-Accounts, and not
            on an individual Account basis.
          </LI>
          <LI>
            At the beginning of each calendar year, the Trade Member&apos;s Net
            Spend will be reset to zero, provided that, their Status Tier at the
            end of the year prior to the reset will be retained as their minimum
            Status Tier through the new calendar year. Trade Members can
            maintain their current Status Tier or unlock a higher Status Tier by
            meeting the applicable minimum Net Spend for their current or such
            higher Status Tier during the new calendar year.
          </LI>
          <LI>
            If a Trade Member does not meet the minimum Net Spend for their
            current Status Tier or a higher Status Tier at the end of such
            calendar year, for the next year, the Trade Member will receive the
            applicable Status Tier for their actual Net Spend.
          </LI>
        </ol>
      </LI>
      <LI>
        <Strong>Trade Member Discount</Strong>
        <ol style={{ listStyleType: 'lower-roman' }}>
          <LI>
            Eligible Trade Members will receive the applicable discount
            displayed on the Product page on full-price Products available
            through The Expert Marketplace or a Trade Vendor at the
            then-published retail price.
          </LI>
          <LI>
            The discounts will be displayed in the Marketplace for Trade Members
            and Team Members logged into their Account. Trade Members and their
            Team Members must be signed into their Accounts to receive the
            discount on purchases.
          </LI>
          <LI>
            The discount is not applicable to purchases made prior to enrollment
            in the Trade Program or purchases of gift cards (electronic,
            physical, or otherwise), services, shipping and handling, taxes or
            other fees, and non-merchandise charges. Certain Products may be
            excluded or exempt from the discount. The discount may not be
            combined with any other offers, coupons or promotions, and cannot be
            applied to reduced price, sale, closeout or clearance merchandise.
          </LI>
          <LI>
            The discount is to be used for business purposes only. Using the
            discount for personal purchases is a violation of this Agreement.
          </LI>
        </ol>
      </LI>

      <LI>
        <Strong>Purchase and Payments Terms.</Strong> For the avoidance of
        doubt, purchases under the Trade Program are subject to{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
          target="_blank"
          rel="noreferrer"
        >
          Purchase Terms and Conditions
        </a>{' '}
        in addition to this Agreement.
      </LI>
      <LI>
        <Strong>Tax Exemptions.</Strong> Subject to the terms and conditions of
        this Agreement, Trade Members that have valid reseller licenses and/or
        certificates (hereinafter, a{' '}
        <Strong>&ldquo;Reseller License&rdquo;</Strong>) are eligible to receive
        a sales tax exemption on Products purchased under the Trade Program.
        Trade Member must submit its valid Reseller License(s), including any
        renewals thereof, to The Expert after which the Trade Member will have a
        sales tax exemption status under the Trade Account. Trade Member is
        solely responsible for obtaining and maintaining valid Reseller
        Licenses.
      </LI>
      <LI>
        After submission of the Reseller License(s) to The Expert, Trade
        Member&apos;s sales tax exemption status will continue until the earlier
        to occur of: (i) the expiration date of the applicable Reseller License,
        and (ii) 3 years from the date the sale tax exemption status was granted
        by The Expert for the Reseller License. Trade Member must provide a new
        Reseller License, or resubmit its Reseller License, to maintain its
        sales tax exemption status.
      </LI>
      <LI>
        Notwithstanding the foregoing, The Expert reserves the right to require
        Trade Member to, and Trade Member shall upon such request, resubmit its
        Reseller License(s) at any time for any reason to maintain Trade
        Member&apos;s sales tax exemption status.
      </LI>
      <LI>
        Failure to provide updated Reseller License(s), or to resubmit Reseller
        License(s), whether upon expiration of Trade Member&apos;s sales tax
        exemption status or upon The Expert&apos;s request for resubmission,
        shall result in the suspension or termination of Trade Member&apos;s
        sales tax exemption status (until such Reseller License(s) are
        provided).
      </LI>
      <LI>
        The Trade Member must have an active sales tax exemption status at the
        time of purchase in order to receive a sales tax exemption. In addition,
        Trade Member must have a valid Reseller License in the State where the
        Product(s) will be shipped in order to receive the sales tax exemption
        on such Product(s).
      </LI>
      <LI>
        If you submit any Reseller License(s), you represent, warrant and
        covenant, at all times, that such Reseller License(s) are (i) valid
        under the State(s) in which such Reseller License(s) are issued, and
        (ii) accurate, true, complete and up to date. You agree to and shall
        indemnify, defend and hold harmless The Expert, its officers, employees,
        agents, directors, subsidiaries, affiliates, successors, assigns,
        suppliers, or licensors from any and all penalties, fines, taxes,
        assessments, claims, damages, liabilities, losses, costs and expenses
        (including attorneys&apos; reasonable fees) arising from or related to
        your breach of the foregoing, including, but not limited to, any invalid
        Reseller License submitted by you.
      </LI>
    </ol>
    <Paragraph>
      <Strong>6. Trade Program Referrals</Strong>
    </Paragraph>
    <ol style={{ listStyleType: 'lower-alpha' }}>
      <LI>
        Subject to terms and conditions of this Agreement, Trade Members may
        permit their Team Members to refer another individual, entity or
        organization to apply to the Trade Program. Upon acceptance of the
        Applicant to the Trade Program as set forth above, the referring Team
        Member and the referred Applicant will each be eligible to receive a
        referral bonus of $200 on a minimum purchase of $500 (the{' '}
        <Strong>&ldquo;Referral Bonus&rdquo;</Strong>). For clarity, Trade
        Members will be jointly and severally responsible and liable for
        referrals made by Team Members, and compliance with these terms or any
        breach or violation thereof.
      </LI>
      <LI>
        Team Members may only refer persons, companies, or entities that meet
        the following (collectively,{' '}
        <Strong>&ldquo;Eligible Referrals&rdquo;</Strong>) (i) are engaged in a
        business, industry, or services that could benefit from participation in
        the Trade Program, (ii) the Team Member knows or are otherwise connected
        to, but expressly excluding, any person, company, or entity that has a
        current engagement, contract and/or employment relationship with the
        Team Member, (iii) are not government officials, or otherwise government
        owned or controlled and/or any of their employees, (iv) are not
        political parties and/or candidates, and/or (v) are not representatives,
        agents or employees of The Expert.
      </LI>
      <LI>
        Team Members shall not, nor enable any third party to: (i)
        &ldquo;spam&rdquo; anyone with referral invitations or emails,
        including, without limitation, mass emailing, texting or messaging
        people the Team Member does not know; (ii) use automated systems or bots
        through any channel to distribute the referral invitations or emails; or
        (iii) include any material and/or content (including, without
        limitation, any text of email distributions) with any referral
        invitation and/or email that is false, inaccurate, illegal or otherwise
        unlawful, abusive, harassing, harmful to reputation, pornographic,
        indecent, profane, obscene, hateful, racist, infringing, libelous,
        tortious, or otherwise objectionable in The Expert&apos;s sole opinion,
        or that violates any the rights of any third party.
      </LI>
      <LI>
        The referred Applicant and the referring Team Member will only be
        eligible to receive a Referral Bonus for a{' '}
        <Strong>&ldquo;Qualified Referral&rdquo;</Strong> where the referred
        Applicant: (i) is an Eligible Referral, (ii) is accepted by The Expert
        into the Trade Program, (iii) is not an existing registered participant
        in the Trade Program, or otherwise already in The Expert&apos;s systems,
        (iv) has not already been referred to the Trade Program by another
        member of the Trade Program, and (v) with respect to a Team
        Member&apos;s eligibility for a Referral Bonus, the referred Applicant
        makes a purchase through the Marketplace within 60 days of being
        accepted to the Trade Program.{' '}
        <Strong>
          The Expert reserves the right, in its sole discretion, to determine
          whether or not a referral is a Qualified Referral and to withhold and
          not issue any Referral Bonus to the referred Applicant and/or the
          referring Team Member where The Expert determines, in its sole
          discretion, that such referrals or actions of the referred Application
          and/or referring Team Member are not consistent with the intent of the
          Trade Program referral terms, or are in violation of this Agreement.
        </Strong>
      </LI>
      <LI>
        Subject to the terms and conditions of this Agreement, including, but
        not limited to Section 6(d) above, the Referral Bonus will be issued:
        (i) to the referred Applicant that is a Qualified Referral upon The
        Expert&apos;s acceptance of the Applicant to the Trade Program, and (ii)
        to the referring Team Member upon the date the referred Applicant meets
        all the requirements of a Qualified Referral. For the avoidance of
        doubt, Referral Bonuses issued to the referred Applicant that is a
        Qualified Referral will be posted to the Primary User of the Trade
        Account, and not to any individual Team Member. Referral Bonuses issued
        to a referring Team Member for a Qualified Referral will be posted to
        the referring Team Member&apos;s Account.
      </LI>
      <LI>
        Each Referral Bonus shall expire 60 days from the date Referral Bonus is
        posted to the Trade Account or referring Team Member&apos;s Account, as
        applicable. Notwithstanding the foregoing, Referral Bonuses shall
        automatically expire and be void upon termination of this Agreement for
        any reason.
      </LI>
      <LI>
        Referral Bonuses may be used on eligible purchases made through the
        Marketplace.{' '}
        <Strong>
          Referral Bonuses may not be used towards the purchase of Consultations
          or products from a Trade Vendor and may be subject to additional
          restrictions or limitations specified by The Expert.
        </Strong>
        Purchases are subject to{' '}
        <a
          href="https://www.theexpert.com/terms"
          target="_blank"
          rel="noreferrer"
        >
          The Expert Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://www.theexpert.com/blog/purchase-terms-and-conditions"
          target="_blank"
          rel="noreferrer"
        >
          Purchase Terms and Conditions
        </a>{' '}
        in addition to this Agreement. Referral Bonuses are not transferable
        from the Trade Account or referring Team Member&apos;s Account, as
        applicable, including, but not limited to, any Team Member.
      </LI>
      <LI>
        Referral Bonuses are non-negotiable, have no cash value, cannot be
        exchanged for cash or credit, nor shall any substitutions or partial
        acceptance be permitted. Referral Bonuses may not be purchased or sold,
        and do not constitute property. Neither Trade Members nor Team Members
        have any ownership interest in Referral Bonuses. Any receipt or use of
        Referral Bonuses in violation of this Agreement, will render such
        Referral Bonuses void. Team Members and the Trade Members (including,
        the referred Applicant) are responsible for any federal, state and/or
        local taxes, and any other costs and expenses, if any, that may result
        from the redemption or use of any Referral Bonuses.
      </LI>
    </ol>

    <Paragraph>
      <Strong>7. Changes.</Strong> The Expert reserves the right to, and may, at
      any time and for any reason in its sole discretion, change, suspend or
      discontinue the Trade Program or any part thereof, including, but not
      limited to, requirements and information for applications to the Trade
      Program, the Trade Perks and terms and conditions for earning,
      eligibility, and/or availability of any Trade Perks, and/or its terms and
      conditions regarding Trade Program Referrals. You acknowledge and agree
      that The Expert will not be liable to you, any Team Member, or any other
      any third party for any modification, suspension or discontinuance of the
      Trade Program or any part thereof.
    </Paragraph>
    <Paragraph>
      <Strong>8. Term; Termination</Strong>
      <ol style={{ listStyleType: 'lower-alpha' }}>
        <LI>
          This Agreement will remain in full force and effect until terminated
          in accordance with the provisions of this Agreement.
        </LI>
        <LI>
          Either party may terminate this Agreement upon written notice to the
          other. For termination notices from you to The Expert, please notify
          us at <a href="mailto:hello@theexpert.com">hello@theexpert.com</a>;
          subject line: “Notice of Termination – Trade Program Agreement.”
          Notices to you will be sent to the email address registered under the
          Trade Account.
        </LI>
        <LI>
          Notwithstanding the anything to the contrary herein, in addition to
          any of The Expert&apos;s other rights or remedies, The Expert may
          immediately and without notice to you, terminate this Agreement, or
          suspend your Trade Account or the Account of any Team Member, if The
          Expert, in good faith, believes that you or any Team Member have
          violated or breached, this Agreement or{' '}
          <a
            href="https://www.theexpert.com/terms"
            target="_blank"
            rel="noreferrer"
          >
            The Expert Terms of Service
          </a>
          , including any incorporated policies, guidelines, terms or rules
          herein or therein.
        </LI>
        <LI>
          Upon termination, your right to participate in the Trade Program shall
          immediately terminate, and you shall immediately cease, and ensure all
          Team Members cease, all participation in the Trade Program. All Trade
          Perks, including any Trade Notes posted prior to the effective date of
          termination, shall automatically expire and be void upon termination
          of this Agreement for any reason. The following Sections will survive
          any expiration or termination of this Agreement: 1, 3(a), 4(d), 6,
          7(d), 8, 9, 10, 11, 12, 13 and 14.
        </LI>
      </ol>
    </Paragraph>
    <Paragraph>
      <Strong>9. Confidentiality.</Strong> You acknowledge that the terms of
      this Agreement and any non-public information regarding the business of
      The Expert, and its products, and services shall be confidential (
      <Strong>&ldquo;Confidential Information&rdquo;</Strong>), and you agree
      that you will not disclose Confidential Information to any third party,
      other than Team Members.
    </Paragraph>
    <Paragraph>
      <Strong>10. Indemnity.</Strong> You agree to and shall indemnify, defend
      and hold harmless The Expert, its officers, employees, agents, directors,
      subsidiaries, affiliates, successors, assigns, suppliers, or licensors
      from any and all claims, actions, suits, demands, damages, liabilities,
      losses, costs and expenses (including attorneys&apos; reasonable fees)
      arising from or related to: (a) your or any Team Member&apos;s violation
      of this Agreement (including, without limitation, any breach of your
      obligations, representations and warranties made in this Agreement); (b)
      any claims brought by Team Members; and/or (c) allegations that your
      activities in connection with, or participation in, the Trade Program,
      including, any referrals to the Trade Program, violates any applicable
      laws, rules or regulations, including, without limitation, the CAN-SPAM
      Act or other anti-SPAM laws, and/or any applicable data protection laws or
      regulations. The Expert will have sole control of the defense and any
      related settlement negotiations of such claims. You may participate and
      retain your own counsel at your expense.
    </Paragraph>
    <Paragraph>
      <Strong>11. Disclaimer.</Strong> THE EXPERT MAKES NO WARRANTIES UNDER THIS
      AGREEMENT AND HEREBY EXPRESSLY DISCLAIMS ANY REPRESENTATIONS, WARRANTIES
      OR GUARANTEES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF
      MERCHANTABILITY, FITNESS FOR ANY PARTICULAR PURPOSE, SATISFACTORY PURPOSE,
      ACCURACY, OR NON-INFRINGEMENT. THE EXPERT MAKES, AND HAS MADE, NO
      REPRESENTATIONS, WARRANTIES, OR GUARANTEES THAT THE TRADE PROGRAM OR ANY
      TRADE PERKS WILL BE UNINTERRUPTED OR ERROR-FREE OR MEET THE TRADE
      MEMEBR&apos;S OR ANY TEAM MEMBER&apos;S REQUIREMENTS. PARTICIPATION IN THE
      TRADE PROGRAM AND USE, RECEIPT OR REDEMPTION OF ANY TRADE PERKS BY TRADE
      MEMBER AND/OR ANY OF ITS TEAM MEMBERS AT THE SOLE AND ENTIRE RISK OF TRADE
      MEMBER AND ITS TEAM MEMBERS.
    </Paragraph>
    <Paragraph>
      <Strong>12. Limitation of Liability.</Strong> IN NO EVENT WILL THE EXPERT
      BE LIABLE TO YOU OR ANY THIRD PARTY OR PERSON FOR ANY LOST PROFITS, LOST
      REVENUES OR LOSS OF BUSINESS ADVANTAGE, COST OF COVER, OR ANY INDIRECT,
      SPECIAL, INCIDENTAL OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED TO
      THIS AGREEMENT, WHETHER BASED ON BREACH OF CONTRACT, TORT (INCLUDING
      NEGLIGENCE) OR OTHERWISE, AND WHETHER OR NOT YOU HAVE BEEN ADVISED OF THE
      POSSIBILITY OF SUCH DAMAGE. THE EXPERT&apos;S TOTAL AGGREGATE LIABILITY TO
      YOU FOR ANY DAMAGES OR ALLEGED DAMAGES ARISING FROM OR RELATED TO THIS
      AGREEMENT, WHETHER IN CONTRACT, TORT OR ANY OTHER LEGAL THEORY, SHALL NOT
      EXCEED TWENTY-FIVE DOLLARS (USD $25). THE LIMITATIONS SPECIFIED IN THIS
      PARAGRAPH SHALL SURVIVE AND APPLY EVEN IF ANY LIMITED REMEDY SPECIFIED IN
      THIS AGREEMENT IS FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE.
    </Paragraph>
    <Paragraph>
      <Strong>13. Electronic Communications.</Strong> You consent to receiving
      electronic communications from The Expert, including, without limitation,
      notices posted on our website and/or via email. These electronic
      communications may include notices about applicable fees and charges,
      transactional information and other information concerning or related to
      your participation in the Trade Program and/or any Trade Perks. These
      electronic communications are part of your relationship with The Expert
      and you receive them as part of your participation in the Trade Program.
      You agree that any notices, agreements, disclosures or other
      communications that we send to you electronically will satisfy any legal
      communication requirements, including that such communications be in
      writing.
    </Paragraph>
    <Paragraph>
      <Strong>14. Governing Law; Dispute Resolution</Strong>
      <ol style={{ listStyleType: 'lower-alpha' }}>
        <LI>
          <span style={{ textDecoration: 'underline' }}>
            Governing Law; Dispute Resolution.
          </span>{' '}
          This Agreement shall be governed in all respects by and construed in
          accordance with the laws of the State of New York, without regard to
          its conflicts of laws principles. Any controversy or claim arising out
          of or relating to this Agreement, or any breach thereof, shall be
          determined and settled by arbitration in Manhattan, New York, pursuant
          to the rules then pertaining of the American Arbitration Association
          as such rules are then prevailing, provided that the arbitrator and
          the parties shall comply with the following: (a) the arbitration shall
          be conducted by telephone, online and/or be solely based on written
          submissions, the specific manner shall be chosen by the party
          initiating the arbitration; (b) the arbitration shall not involve any
          personal appearance by the parties or witnesses unless otherwise
          mutually agreed by the parties; and (c) any judgment on the award
          rendered by the arbitrator shall be binding, final, and confidential,
          and may be entered in any court of competent jurisdiction. You and The
          Expert will share equally the administrative costs of such arbitration
          proceedings.
        </LI>
        <LI>
          <span style={{ textDecoration: 'underline' }}>
            Venue; Exceptions to Arbitration.
          </span>{' '}
          If the agreement to arbitrate is found not to apply your or our claim,
          you and The Expert agree that any judicial proceeding will be brought
          in the federal or state courts of New York, New York. Both you and The
          Expert consent to venue and personal jurisdiction there.
          Notwithstanding anything in this Agreement to the contrary to the
          extent you have in any manner violated or threatened to violate any of
          The Expert’s intellectual property rights, The Expert may seek
          injunctive or other appropriate relief in any state or federal court
          with competent jurisdiction in any country, including in the State of
          New York, United States of America, without first engaging in
          arbitration or the informal dispute process set forth in this Section,
          and you hereby consent to the personal jurisdiction and exclusive
          venue in such courts
        </LI>
      </ol>
    </Paragraph>
    <Paragraph>
      <Strong>15. Modifications to this Agreement.</Strong> The Expert reserves
      the right to update or modify this Agreement at any time. Except as
      otherwise set forth in this Agreement or as stated below with respect to
      material changes, all updates and modifications to this Agreement will be
      effective from the day they are posted online at{' '}
      <a
        href="https://www.theexpert.com/trade-program-agreement"
        target="_blank"
        rel="noreferrer"
      >
        Trade Program Agreement
      </a>
      . If we make any material changes to this Agreement, we will provide you
      reasonable prior notice of such changes by sending a notification to the
      email address we have on file for you. Material changes to this Agreement
      will become effective on the date set forth in the notice. It is your
      responsibility to regularly visit and review this Agreement for updates,
      changes and modification. If you do not agree to any updates or
      modifications to this Agreement, simply terminate this Agreement. If you
      do not terminate this Agreement prior to the effective date of the revised
      Agreement, or you or any Team Member continues to participate in the Trade
      Program after the effective date of the revised Agreement, that will
      constitute your acceptance of the revised Agreement.
    </Paragraph>
    <Paragraph>
      <Strong>16. General.</Strong> Your relationship with The Expert is that of
      an independent contractor and nothing in this Agreement is intended to, or
      shall be construed to, create a joint venture, partnership, agency,
      employment, or similar relationship between the parties. Neither the
      rights nor the obligations arising under this Agreement are assignable by
      you, and any such attempted assignment or transfer shall be void and
      without effect. The Expert may freely assign this Agreement and/or any of
      its rights and obligations under this Agreement. Any waiver of any
      provision of this Agreement must be in writing and executed by both
      parties. The failure of either party to exercise any right provided for by
      this Agreement shall not be deemed a waiver of that right. Except as
      otherwise specified in this Agreement, this Agreement may be amended or
      supplemented only by a writing that refers explicitly to this Agreement
      and that is signed on behalf of both parties. If any term or provision of
      this Agreement is determined to be illegal, unenforceable, or invalid in
      whole or in part for any reason, that provision shall be stricken from
      this Agreement and shall not affect the legality, enforceability or
      validity of the remainder of the provisions set forth in this Agreement.
      This Agreement is not intended to grant rights to anyone except you and
      The Expert, and this Agreement shall not create any third party
      beneficiary rights. This Agreement, and any other policies or terms and
      conditions referenced herein, constitutes the entire agreement between you
      and The Expert, and supersedes all prior oral or written agreements or
      communications, with regard to the subject matter described herein. Any
      notice to you may be provided by email. The headings of Sections of this
      Agreement are for convenience and are not to be used in interpreting this
      Agreement. As used in this Agreement, the word “including” means
      “including but not limited to.” You agree that this Agreement will not be
      construed against The Expert by having drafted it. The official text of
      this Agreement (and any notice submitted hereunder) will be in English.
      The parties acknowledge that they require that this Agreement be drawn up
      in the English language only. In the event of any dispute concerning the
      construction or meaning of this Agreement, reference will be made only to
      this Agreement as written in English and not to any translation into
      another language.
    </Paragraph>
    <Paragraph>
      <Strong>QUESTIONS AND ADDITIONAL INFORMATION.</Strong> Please feel free to
      contact us at <a href="mailto:hello@theexpert.com">hello@theexpert.com</a>{' '}
      if you have any questions about this Agreement.
    </Paragraph>
    <PureSSRIndicator />
  </Box>
)

export function getStaticProps() {
  return prepareGetStaticNavigationProps()
}

TradeProgramAgreement.getLayout = (page) =>
  getPublicLayout(page, { isWithBreadcrumbs: true })
